import React, { useEffect, useState } from 'react';
import {
  getPublicContactSettings,
  FALLBACK_CONTACT_SETTINGS,
  type ContactSettings,
} from '../features/contactSettings';

interface ContactModalProps {
  show: boolean;
  onHide: () => void;
}

function telHref(number: string): string {
  const digits = number.replace(/\D/g, '');
  return digits ? `tel:${digits}` : '#';
}

function whatsappHref(number: string): string {
  const digits = number.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
}

const ContactModal: React.FC<ContactModalProps> = ({ show, onHide }) => {
  const [settings, setSettings] = useState<ContactSettings>(FALLBACK_CONTACT_SETTINGS);

  useEffect(() => {
    if (!show) return;
    getPublicContactSettings()
      .then(setSettings)
      .catch(() => setSettings(FALLBACK_CONTACT_SETTINGS));
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, onHide]);

  if (!show) return null;

  return (
    <div
      className="modal show d-block site-modal"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onHide}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content site-modal__content">
          <div className="modal-header site-modal__header">
            <div>
              <h5 className="modal-title site-modal__title mb-1" id="contact-modal-title">
                Contact BMCS
              </h5>
              <p className="site-modal__subtitle mb-0">Choose how you&apos;d like to get in touch</p>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
          </div>

          <div className="modal-body site-modal__body">
            <div className="d-grid gap-2">
              <a href={telHref(settings.contactNumber)} className="btn site-modal__submit w-100">
                <i className="bi bi-telephone me-2" aria-hidden />
                Call {settings.contactNumber}
              </a>
              <a
                href={whatsappHref(settings.whatsappNumber)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn site-modal__secondary w-100"
              >
                <i className="bi bi-whatsapp me-2" aria-hidden />
                WhatsApp {settings.whatsappNumber}
              </a>
              <a href={`mailto:${settings.emailAddress}`} className="btn site-modal__secondary w-100">
                <i className="bi bi-envelope me-2" aria-hidden />
                Email {settings.emailAddress}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
